// useResponsiveLayout.ts
// Custom hook to manage responsive layout behavior and scale factors

import { useState, useEffect } from 'react';

interface UseResponsiveLayoutReturn {
  windowWidth: number;
  scaleFactor: number;
  setScaleFactor: (factor: number | ((prev: number) => number)) => void;
  viewMode: 'salon' | 'master';
  setViewMode: (mode: 'salon' | 'master') => void;
}

const MOBILE_BREAKPOINT = 900;
const DESKTOP_BREAKPOINT = 1400;
const MOBILE_SCALE_FACTOR = 0.7;
const DESKTOP_SCALE_FACTOR = 1.13;

export const useResponsiveLayout = (): UseResponsiveLayoutReturn => {
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);
  const [scaleFactor, setScaleFactor] = useState<number>(
    window.innerWidth <= MOBILE_BREAKPOINT ? MOBILE_SCALE_FACTOR : DESKTOP_SCALE_FACTOR
  );
  const [viewMode, setViewMode] = useState<'salon' | 'master'>('salon');

  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      setWindowWidth(w);

      // Scale factor: force specific values based on viewport
      // Mobile (<=900px): always 0.7 to ensure ALL content fits within borders
      // Desktop (>900px): 1.13 default (can be overridden by user zoom in ActionSection)
      if (w <= MOBILE_BREAKPOINT) {
        // Force mobile scale lower to ensure grid fits completely
        setScaleFactor(MOBILE_SCALE_FACTOR);
      } else {
        // On desktop, keep current scale (user may have zoomed)
        // Only reset to default if not already set
        setScaleFactor(prev => prev < 0.9 ? DESKTOP_SCALE_FACTOR : prev);
      }

      // Auto-adjust view mode based on width
      if (w > DESKTOP_BREAKPOINT) {
        setViewMode('salon');
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Initial check
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return {
    windowWidth,
    scaleFactor,
    setScaleFactor,
    viewMode,
    setViewMode,
  };
};
